import React, { FC, FocusEventHandler, useState } from "react";
import Select, { components, SingleValue } from "react-select";
import { Collapse } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

import { Text } from "../../../shared/uiComponents";
import { red500 } from "../../../shared/Helpers/colors";
import { black500, white100, black900, blue400 } from "../../../shared/Helpers/colors";

import { InsuranceProperties } from "../../../redux/API/ClientAPIHelpers/insuranceAuthorizationProperties";

interface SelectOption {
  value: string;
  label: string;
  isSubMenu?: boolean;
  originalItem?: InsuranceProperties;
  isSubItem?: boolean;
  isDisabled?: boolean;
}

const CustomOption = (props: any) => {
  const { data, innerProps, isFocused, isDisabled, isSelected } = props;

  const optionBaseStyle = {
    padding: "8px 12px",
    backgroundColor: isSelected
      ? "rgba(0, 0, 0, 0.08)" // Background for selected item
      : isFocused
      ? "rgba(0, 0, 0, 0.04)" // Background for hovered item
      : "transparent",
    color: isDisabled ? "rgba(0, 0, 0, 0.38)" : "inherit",
    opacity: isDisabled ? 0.7 : 1,
    cursor: isDisabled ? "not-allowed" : "pointer",
    "&:active": {
      backgroundColor: isDisabled ? "transparent" : "rgba(0, 0, 0, 0.08)",
    },
  };

  return (
    <div
      {...innerProps}
      style={{
        ...optionBaseStyle,
        paddingLeft: data.isSubItem ? "30px" : "8px 12px",
      }}
    >
      <Text title={data.label} />
    </div>
  );
};

const CustomMenuList = (props: any) => {
  return (
    <components.MenuList {...props}>
      {props.children}
      {props.selectProps.loadMore && props.selectProps.loadMore.activate && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "8px 12px",
            cursor: "pointer",
            borderTop: "1px solid #eee",
            marginTop: "4px",
          }}
          onClick={() => props.selectProps.loadMore.setSize((prev: number) => prev + 8)}
        >
          <Text title="Load More" size="smallBold" />
        </div>
      )}
    </components.MenuList>
  );
};

interface SelectProperties {
  data: Array<InsuranceProperties> | null;
  setValue: (value: string | null) => void;
  value: string | null;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  description?: string;
  error?: boolean;
  errorMessage?: string;
  className?: string;
  onBlur?: FocusEventHandler<HTMLInputElement>;
}

const CustomSelect: FC<SelectProperties> = ({
  data,
  value,
  setValue,
  label = "",
  description = "",
  error = false,
  disabled = false,
  errorMessage = "",
  className = "",
  onBlur,
}) => {
  const allSelectableOptions: SelectOption[] = [];
  const groupedOptionsList: {
    label: string;
    options: SelectOption[];
    originalItem: InsuranceProperties;
  }[] = [];

  if (data) {
    data.forEach((item) => {
      if (item.subInsurances && item.subInsurances.length > 0) {
        const subItemsOptions: SelectOption[] = [];
        item.subInsurances.forEach((subInsurance) => {
          const subOption: SelectOption = {
            value: subInsurance.id,
            label: subInsurance.name,
            isSubItem: true, // Mark as a sub-item
            isDisabled: !subInsurance.canBeAssigned,
          };
          allSelectableOptions.push(subOption);
          subItemsOptions.push(subOption);
        });

        groupedOptionsList.push({
          label: item.name,
          options: subItemsOptions,
          originalItem: item,
        });
      } else {
        const topLevelOption: SelectOption = {
          value: item.id,
          label: item.name,
          isDisabled: !item.canBeAssigned,
        };
        allSelectableOptions.push(topLevelOption);
      }
    });
  }

  const finalOptionsForSelect: (
    | SelectOption
    | { label: string; options: SelectOption[]; originalItem: InsuranceProperties }
  )[] = [];

  // First, add all non-grouped top-level options directly to the final list
  data
    ?.filter((item) => !item.subInsurances || item.subInsurances.length === 0)
    .forEach((item) =>
      finalOptionsForSelect.push({
        value: item.id,
        label: item.name,
        isDisabled: !item.canBeAssigned,
      })
    );

  // Then, add the structured groups
  groupedOptionsList.forEach((group) => finalOptionsForSelect.push(group));

  const selectedOption = allSelectableOptions.find((option) => option.value === value);

  const CustomGroup = (props: any) => {
    const { children, label, data } = props;
    const [open, setOpen] = useState<boolean>(false);

    const hasVisibleOptions = React.Children.count(children) > 0;

    if (!hasVisibleOptions) {
      return null;
    }

    const isGroupDisabled = data.options.every((opt: SelectOption) => opt.isDisabled);

    const headerStyle = {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "8px 12px",
      cursor: isGroupDisabled ? "not-allowed" : "pointer",
      color: isGroupDisabled ? "rgba(0, 0, 0, 0.38)" : "inherit",
      opacity: isGroupDisabled ? 0.7 : 1,
      "&:hover": {
        backgroundColor: isGroupDisabled ? "transparent" : "rgba(0, 0, 0, 0.04)",
      },
    };

    return (
      <div style={{ marginBottom: "0" }}>
        <div
          onClick={(e) => {
            e.stopPropagation();
            if (!isGroupDisabled) {
              setOpen(!open);
            }
          }}
          style={headerStyle}
        >
          <Text title={label} />
          {open ? <ExpandLess /> : <ExpandMore />}
        </div>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <div>{children}</div>
        </Collapse>
      </div>
    );
  };

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      border: `1px solid ${black500}`,
      borderRadius: "10px",
      background: white100,
      minHeight: "40px",
      height: "40px",
      width: "100%",
      padding: 0,
      boxShadow: "none",
      cursor: disabled ? "not-allowed" : "pointer",
      transition: "all 0.2s ease",

      borderBottom: error ? `2px solid ${red500}` : `1px solid ${black500}`,
      borderColor: error ? red500 : state.isFocused ? black500 : black500,

      "&:hover": {
        borderColor: error ? red500 : black500,
      },
    }),

    valueContainer: (provided: any) => ({
      ...provided,
      padding: 0,
      height: "100%",
      display: "flex",
      alignItems: "center",
    }),

    input: (provided: any, state: any) => ({
      ...provided,
      padding: "8px 10px",
      margin: 0,
      color: black900,
      boxSizing: "border-box",
      width: "100%",
      height: "100%",
      outline: "none",
      borderRadius: "10px",
      transition: "box-shadow 0.2s ease",

      boxShadow: state.isFocused ? `0 0 0 0.2rem ${blue400}` : "none",
    }),

    placeholder: (provided: any) => ({
      ...provided,
      color: black500,
      padding: "8px 10px",
      margin: 0,
      position: "absolute",
      top: 0,
      left: 0,
      height: "100%",
      display: "flex",
      alignItems: "center",
    }),

    singleValue: (provided: any) => ({
      ...provided,
      color: black900,
      padding: "8px 10px",
      margin: 0,
      position: "absolute",
      top: 0,
      left: 0,
      height: "100%",
      display: "flex",
      alignItems: "center",
    }),

    indicatorSeparator: (provided: any) => ({
      ...provided,
      display: "none",
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      padding: "8px",
      color: black500,
      "&:hover": {
        color: black900,
      },
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999,
      maxWidth: "350px",
      boxShadow:
        "0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)",
      borderRadius: "4px",
      marginTop: "8px",
    }),
    menuList: (provided: any) => ({
      ...provided,
      padding: "8px",
    }),
    option: (provided: any) => ({
      ...provided,
      padding: "0",
      backgroundColor: "transparent",
      "&:active": {
        backgroundColor: "transparent",
      },
    }),
  };

  return (
    <div className={className}>
      <div style={{ width: "100%" }}>
        <Text title={label} className={`inputLabel ${!!label ? "active" : ""}`} size={"tinyBold"} />
        <Select<SelectOption>
          options={finalOptionsForSelect}
          value={selectedOption}
          onChange={(option: SingleValue<SelectOption>) => {
            setValue(option ? option.value : null);
          }}
          isDisabled={disabled}
          onBlur={onBlur}
          styles={customStyles}
          components={{
            Option: CustomOption,
            DropdownIndicator: (props) => (
              <components.DropdownIndicator {...props}>
                {props.selectProps.menuIsOpen ? <ExpandLess /> : <ExpandMore />}
              </components.DropdownIndicator>
            ),
            Group: CustomGroup,
            MenuList: CustomMenuList,
          }}
          isClearable={false}
          placeholder={""}
        />

        {error && errorMessage && (
          <Text className={`error active`} title={errorMessage} size={"tiny"} textColor={red500} />
        )}
        {!error && description && (
          <Text className={`inputDescription active`} title={description} size={"tiny"} />
        )}
      </div>
    </div>
  );
};

export default CustomSelect;
